import React, { useState } from 'react';
import { X, Plus, Trash } from 'lucide-react';
import { type Word } from '../../types';

interface WordFormProps {
  word: Partial<Word & {
    example1?: string;
    example1_translation?: string;
    example1_persian_translation?: string;
    example2?: string;
    example2_translation?: string;
    example2_persian_translation?: string;
    example3?: string;
    example3_translation?: string;
    example3_persian_translation?: string;
  }>;
  onSave: (e: React.FormEvent) => void;
  onChange: (word: Partial<Word>) => void;
  onClose: () => void;
}

export function WordForm({ word, onSave, onChange, onClose }: WordFormProps) {
  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">{word.id ? 'Edit Word' : 'New Word'}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <form onSubmit={onSave} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Dutch Word</label>
              <input
                type="text"
                value={word.dutch}
                onChange={(e) => onChange({ ...word, dutch: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">English Translation</label>
              <input
                type="text"
                value={word.translation}
                onChange={(e) => onChange({ ...word, translation: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Persian Translation</label>
            <input
              type="text"
              value={word.persian_translation}
              onChange={(e) => onChange({ ...word, persian_translation: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Pronunciation</label>
            <input
              type="text"
              value={word.pronunciation}
              onChange={(e) => onChange({ ...word, pronunciation: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              placeholder="e.g., hah-loh"
            />
          </div>

          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h4 className="text-sm font-medium text-gray-700">Example Sentences</h4>
              <p className="text-sm text-gray-500">Add up to 3 example sentences</p>
            </div>
            
            {[1, 2, 3].map((num) => (
              <div key={num} className="space-y-2">
                <span className="text-sm text-gray-500">Example {num}</span>
                <input
                  type="text"
                  value={word[`example${num}` as keyof typeof word] || ''}
                  onChange={(e) => onChange({
                    ...word,
                    [`example${num}`]: e.target.value,
                    // Clear translation if example is empty
                    [`example${num}_translation`]: e.target.value ? word[`example${num}_translation` as keyof typeof word] : ''
                  })}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder="Dutch sentence"
                />
                <input
                  type="text"
                  value={word[`example${num}_translation` as keyof typeof word] || ''}
                  onChange={(e) => onChange({
                    ...word,
                    [`example${num}_translation`]: e.target.value,
                    // Clear example if translation is empty
                    [`example${num}`]: e.target.value ? word[`example${num}` as keyof typeof word] : ''
                  })}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder="English Translation"
                />
                <input
                  type="text"
                  value={word[`example${num}_persian_translation` as keyof typeof word] || ''}
                  onChange={(e) => onChange({
                    ...word,
                    [`example${num}_persian_translation`]: e.target.value
                  })}
                  className="mt-2 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder="Persian Translation"
                />
              </div>
            ))}
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
            >
              Save Word
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}